/**
 * Card sections — the single source of truth for how the deck builder groups
 * cards. Sections are organised by role, not power tier:
 *
 *   Classic   — the standard chess army you already know
 *   Aggro     — cheap forward attackers
 *   Special   — pieces with unusual movement or turn mechanics
 *   Terrain   — board-shaping walls, plus Demolish to clear them
 *   Action    — one-shot ability cards that bend the rules for a turn
 *
 * Membership lives here and nowhere else; `packOfPiece` / `packOfAbility` are
 * derived. Adding a card without listing it in a section is caught by the tests.
 */
import type { AbilityId, PackId, PieceTypeId } from "./types";

export interface PackDef {
  readonly id: PackId;
  readonly name: string;
  /** One-line theme, shown as a section subtitle. */
  readonly tagline: string;
  /** How this pack expands the game and builds on the earlier ones. */
  readonly description: string;
  /** Deck-buildable pieces this pack contributes (royalty is never in a pack). */
  readonly pieces: readonly PieceTypeId[];
  /** Ability cards this pack contributes. */
  readonly abilities: readonly AbilityId[];
}

/** Sections in deck-builder display order. */
export const PACKS: readonly PackDef[] = [
  {
    id: "classic",
    name: "Classic",
    tagline: "The chess army you already know.",
    description:
      "The five standard chess pieces. Your King and a Prince always start on " +
      "the board — capture the enemy Prince to expose their King.",
    pieces: ["pawn", "knight", "bishop", "rook", "queen"],
    abilities: [],
  },
  {
    id: "aggro",
    name: "Aggro",
    tagline: "Press forward and force the issue.",
    description:
      "Cheap, oddly-moving foot pieces built to rush the board: diagonal Farmers " +
      "and one-way Lances that only ever push forward.",
    pieces: ["farmer", "lance"],
    abilities: [],
  },
  {
    id: "special",
    name: "Special",
    tagline: "Pieces that break the mould.",
    description:
      "Unusual pieces with their own quirks: a Captain that moves for free every " +
      "turn, an Outpost that plants a forward deployment node, and a Camel that " +
      "leaps even further than a Knight.",
    pieces: ["captain", "outpost", "camel"],
    abilities: [],
  },
  {
    id: "terrain",
    name: "Terrain",
    tagline: "Reshape the battlefield.",
    description:
      "Change the ground itself: drop immovable terrain to wall off lanes and " +
      "funnel the enemy — a one-square Mound, two-square Hills, or tall Walls — " +
      "and Demolish to blow any terrain off the board for good.",
    pieces: ["hill", "valley", "ridge"],
    abilities: ["demolish"],
  },
  {
    id: "action",
    name: "Action Cards",
    tagline: "One-shot tricks that bend the rules.",
    description:
      "Spend your turn on an effect instead of a move: Dash and Sidestep nudge a " +
      "piece, Recall returns any piece to its owner's hand, and Resurrection " +
      "raises one of your fallen pieces.",
    pieces: [],
    abilities: ["dash", "sidestep", "recall", "resurrection"],
  },
];

export function packDef(id: PackId): PackDef {
  const pack = PACKS.find((p) => p.id === id);
  if (!pack) throw new Error(`Unknown pack: ${id}`);
  return pack;
}

const PIECE_PACK = new Map<PieceTypeId, PackId>();
const ABILITY_PACK = new Map<AbilityId, PackId>();
for (const pack of PACKS) {
  for (const piece of pack.pieces) PIECE_PACK.set(piece, pack.id);
  for (const ability of pack.abilities) ABILITY_PACK.set(ability, pack.id);
}

/** The pack a deck-buildable piece belongs to, or undefined for royalty. */
export function packOfPiece(id: PieceTypeId): PackId | undefined {
  return PIECE_PACK.get(id);
}

/** The pack an ability card belongs to. */
export function packOfAbility(id: AbilityId): PackId | undefined {
  return ABILITY_PACK.get(id);
}
